<template>
  <div
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 safe-top safe-bottom"
    role="dialog"
    aria-modal="true"
    aria-labelledby="celebration-title"
    aria-describedby="celebration-description"
    tabindex="-1"
    @click="handleBackdropClick"
    @keydown.escape="$emit('close')"
  >
    <Card
      class="max-w-sm sm:max-w-md w-full text-center p-6 sm:p-8 relative overflow-hidden"
      style="
        background: linear-gradient(135deg, var(--color-neutral-base) 0%, #fefefe 100%);
        border: 2px solid var(--color-accent);
      "
      @click.stop
    >
      <!-- Medal Icon -->
      <div class="mb-4">
        <div
          class="inline-flex items-center justify-center w-16 h-16 rounded-full mb-2"
          style="background: linear-gradient(45deg, var(--color-gold), #ffeb3b)"
        >
          <Trophy class="h-8 w-8 text-yellow-800" />
        </div>
      </div>

      <!-- Title and Description -->
      <h2
        id="celebration-title"
        class="text-2xl sm:text-3xl font-extrabold mb-4"
        style="color: var(--color-primary)"
      >
        You're Our Champion!
      </h2>
      <p
        id="celebration-description"
        class="mb-6 text-sm sm:text-base"
        style="color: var(--color-gray-600)"
      >
        Thanks for making DuoNamo amazing! Your dedication to learning names makes our community
        stronger.
      </p>

      <!-- Action Button -->
      <Button
        ref="closeButton"
        variant="primary"
        class="w-full text-sm sm:text-base px-6 py-3 font-semibold"
        style="background: var(--color-accent); color: var(--color-neutral-base)"
        @click="handleClose"
      >
        <Sparkles class="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
        Continue Playing
      </Button>

      <!-- Confetti Canvas -->
      <canvas
        ref="canvas"
        class="pointer-events-none absolute inset-0 rounded-lg"
        aria-hidden="true"
      />
    </Card>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, nextTick } from 'vue'
import confetti from 'canvas-confetti'
import { Sparkles, Trophy } from 'lucide-vue-next'
import Card from '../ui/Card.vue'
import Button from '../ui/Button.vue'

const canvas = ref(null)
const closeButton = ref(null)
let myConfetti = null
let animationId = null

const emit = defineEmits(['close'])

// Focus management for accessibility
onMounted(async () => {
  await nextTick()

  // Focus the close button for keyboard navigation
  if (closeButton.value) {
    closeButton.value.$el?.focus()
  }

  // Initialize confetti with error handling
  initializeConfetti()
})

// Cleanup on unmount
onUnmounted(() => {
  cleanup()
})

function initializeConfetti() {
  try {
    if (canvas.value) {
      myConfetti = confetti.create(canvas.value, {
        resize: true,
        useWorker: false // Better mobile performance
      })

      // Initial burst
      triggerConfetti()

      // Additional bursts for celebration
      setTimeout(() => triggerConfetti(), 500)
      setTimeout(() => triggerConfetti(), 1000)
    }
  } catch (error) {
    console.warn('Confetti animation failed:', error)
  }
}

function triggerConfetti() {
  if (!myConfetti) return

  try {
    const styles = getComputedStyle(document.documentElement)
    const colors = [
      styles.getPropertyValue('--primary').trim(),
      styles.getPropertyValue('--secondary').trim(),
      styles.getPropertyValue('--tertiary').trim(),
      styles.getPropertyValue('--color-gold').trim(),
      styles.getPropertyValue('--color-success').trim()
    ].filter(Boolean)

    myConfetti({
      particleCount: window.innerWidth < 768 ? 100 : 150, // Fewer particles on mobile
      spread: 70,
      origin: { y: 0.6 },
      colors: colors,
      gravity: 0.8,
      scalar: 0.8,
      drift: 0.1
    })
  } catch (error) {
    console.warn('Confetti trigger failed:', error)
  }
}

function handleClose() {
  cleanup()
  emit('close')
}

function handleBackdropClick(event) {
  if (event.target === event.currentTarget) {
    handleClose()
  }
}

function cleanup() {
  if (myConfetti) {
    try {
      myConfetti.reset?.()
    } catch (error) {
      console.warn('Confetti cleanup failed:', error)
    }
    myConfetti = null
  }

  if (animationId) {
    cancelAnimationFrame(animationId)
    animationId = null
  }
}
</script>

<style scoped>
/* Enhanced mobile responsiveness */
@media (max-width: 640px) {
  .celebration-modal {
    padding: 1rem;
  }
}

/* Smooth animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.celebration-card {
  animation: fadeIn 0.3s ease-out;
}

/* Focus styles for accessibility */
button:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}
</style>
